import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collectCreatorMetrics } from '../api/apifyMetrics';
import {
  createInfluencerFromModal,
  getInfluencers,
  patchPlatformFollowers,
} from '../api/influencers';
import { getExchangeRates } from '../api/clientPricing';
import { toBackendPlatform } from '../utils/apifyCreator';
import {
  metricsKey,
  loadMetricsCache,
  saveMetricsCache,
  toEntry,
  mergeEntry,
  pruneCache,
} from '../utils/creatorMetricsStore';
import { PLATFORM_CONTENT_FORMATS } from '../components/influencer/modals/InfluencerRegisterModal';
import { useToast } from '../hooks/useToast';
import { pickExactCreatorRow } from './pickExactCreatorRow';

// getInfluencers()의 응답 모양이 몇 가지로 갈릴 수 있어(list.js의 normalizeListResponse와 동일 패턴) 방어적으로 처리
function extractRows(res) {
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
}

/**
 * 크리에이터 검색(Apify 수집) + KOLs DB 즉시 등록을 앱 최상단에서 들고 있는 컨텍스트.
 *
 * "추가하기" 흐름 전체(검색 → 확인 → 등록)가 AddCreatorModal 하나 안에서 끝난다 — 별도
 * 확인 폼도, 결과를 모아두는 중간 그리드/팝업도 없음. 국가(batchCountry)와 등록 함수만
 * 여기 앱 루트에 있어서, 모달을 여러 번 열고 닫아도(페이지 이동해도) 유지된다.
 */
const CreatorCollectionContext = createContext(null);

// 활동 콘텐츠는 Apify로는 어떤 포맷을 실제로 하는지 알 수 없어서(그리고 등록 시점엔
// 가격도 없어 의미가 크지 않아서), 하나만 임의로 고르기보다 그 플랫폼이 지원하는 포맷을
// 전부 저장해둔다 — 나중에 실제 캠페인에 쓸 때 필요 없는 것만 빼면 되지, 처음부터 빠져
// 있으면 나중에 추가하기가 더 번거롭다.
function defaultContentTypes(platform) {
  return PLATFORM_CONTENT_FORMATS[platform] ? [...PLATFORM_CONTENT_FORMATS[platform]] : [];
}

// 저장 위치·병합 규칙은 utils/creatorMetricsStore.js 한곳에만 있다.
// 백엔드에 지표 저장 필드가 생기면 그 파일만 갈아끼우면 되고 이 컨텍스트는 그대로다.

export function CreatorCollectionProvider({ children }) {
  const { showSuccess, showError } = useToast();
  const [batchCountry, setBatchCountry] = useState(''); // "추가하기" 모달 상단에서 지정하는 등록 국가
  const [exchangeRates, setExchangeRates] = useState([]);
  // 이미 Apify로 수집해둔 지표(등록 시점 + KolsDB의 수동 "수집" 둘 다 여기로 모음).
  // KolsDB 표의 "참여율" 컬럼이 이걸 먼저 찾아보고 있으면 바로 보여준다.
  const [recentMetrics, setRecentMetrics] = useState(() => loadMetricsCache()); // { "platform:handle": entry }

  // 저장은 항상 mergeEntry를 거친다 — 수집 실패나 빈 값이 멀쩡한 기존 값을 덮지 못하게 하는 지점.
  const setMetricsForCreator = useCallback((item, source = 'apify') => {
    if (!item?.handle) return;
    setRecentMetrics((prev) => {
      const key = metricsKey(item.platform, item.handle);
      const merged = mergeEntry(prev[key], toEntry(item, source));
      const next = pruneCache({ ...prev, [key]: merged });
      saveMetricsCache(next);
      return next;
    });
  }, []);

  // DB 등록 시 통화/세금 계산에 필요 (KolsDBPage 수동 등록 흐름과 동일한 방식으로 조회)
  // 배송사·클라이언트 계정은 KOL을 DB에 등록하지 않아 이 값을 쓸 일이 없고, 단가 API 권한도
  // 없어서 매 화면마다 실패 로그만 쌓였다(배송 상세를 열 때마다 콘솔 에러 4줄). 안 부른다.
  useEffect(() => {
    const serviceType = localStorage.getItem('serviceType') || '';
    if (serviceType.startsWith('DELIVERY') || serviceType === 'CLIENT') return;
    (async () => {
      try {
        const res = await getExchangeRates();
        if (res?.success && res?.data?.exchangeRates) setExchangeRates(res.data.exchangeRates);
      } catch (err) {
        console.error('환율 조회 실패:', err);
      }
    })();
  }, []);

  /* (2026-10-02 지움) 등록 직후 통화 보정 — 서버가 새 KOL 콘텐츠를 USD 로 만들던 것을 화면이 고치려고
     닉네임 «부분 일치»로 찾은 콘텐츠 전부에 단가 0 을 보냈다. 「abc」를 등록하면 「abc_beauty」 단가가
     0 이 될 수 있었고, 그 콘텐츠를 쓰는 캠페인 줄 단가까지 바뀌었다. 이제 서버가 국가 기본 통화로 만든다
     (blbent_bubble 저장 점검 S6). */

  // 닉네임/프로필 URL이 이미 있어서 생성이 막히면: 새로 만들지 않고 기존 레코드의 팔로워 수만
  // 최신 Apify 값으로 갱신한다. 연락처·단가·주소·메모처럼 사람이 직접 채운 값은 절대 안 건드림
  // (그 필드들은 애초에 여기서 patch하지 않으니 자동으로 보존됨).
  const refreshExistingCreator = useCallback(async (item) => {
    const m = item.metrics || {};
    /* 2026-10-12 점검 F12: 닉네임 «포함» 검색의 첫 줄이 아니라 같은 계정 줄만 고친다.
       돌려주는 값 — 'updated'(팔로워를 고침) · 'unchanged'(찾았지만 새 팔로워 값이 없음) · null(못 찾음·실패) */
    try {
      const rows = extractRows(await getInfluencers({ nickname: item.handle }));
      const platformId = pickExactCreatorRow(rows, item)?.platformId;
      if (!platformId) return null;
      if (m.followers == null) return 'unchanged';
      await patchPlatformFollowers(platformId, m.followers);
      return 'updated';
    } catch (err) {
      console.error('기존 크리에이터 갱신 실패:', err);
      return null;
    }
  }, []);

  // 수집된 item을 실제 KOLs DB에 등록(이미 있으면 새로 안 만들고 팔로워만 갱신). 성공/실패를 boolean으로 반환.
  const registerCreator = useCallback(
    async (item) => {
      if (!batchCountry) {
        showError('먼저 국가를 지정해주세요.');
        return false;
      }
      const m = item.metrics || {};
      const platform = toBackendPlatform(item.platform);
      const modalData = {
        representativeName: item.handle || '',
        country: batchCountry,
        channels: [
          {
            platform,
            profileUrl: item.profileUrl || '',
            followers: m.followers != null ? String(m.followers) : '',
            contentFormats: defaultContentTypes(platform),
            memo: '',
          },
        ],
        contacts: (Array.isArray(m.bioContacts) ? m.bioContacts : []).map((c) => ({ channel: c.channel, idOrEmail: c.value })),
      };
      try {
        await createInfluencerFromModal(modalData, exchangeRates);
        setMetricsForCreator(item);
        return true;
      } catch (error) {
        console.error('KOLs DB 등록 실패:', error);
        const errorData = error.response?.data;
        const errorMessage = errorData?.error?.message || errorData?.message || error.message;
        const isDuplicate =
          errorMessage?.includes('프로필 URL 중복') ||
          (errorMessage?.includes('Unique constraint failed') && errorMessage?.includes('profileUrl')) ||
          errorMessage?.includes('already in use');
        if (isDuplicate) {
          // 이미 있는 크리에이터 → 새로 만들지 않고 팔로워만 최신화, 맨 위로 보이도록 갱신 시각을 새로 찍음
          const refreshed = await refreshExistingCreator(item);
          if (refreshed === 'updated') {
            setMetricsForCreator(item);
            showSuccess(`"${item.handle}"는 이미 KOLs DB에 있어서 팔로워 수만 최신화했습니다.`);
            return true;
          }
          if (refreshed === 'unchanged') {
            // 고친 게 없으면 «최신화»라고 하지 않는다(2026-10-12 점검 F12)
            showSuccess(`"${item.handle}"는 이미 KOLs DB에 있습니다. 새 팔로워 값이 없어 바꾸지 않았습니다.`);
            return true;
          }
          showError(`"${item.handle}"은 이미 KOLs DB에 있는데, 같은 계정 줄을 찾지 못했거나 갱신에 실패해 팔로워를 바꾸지 않았습니다.`);
          return false;
        }
        showError(errorMessage || `"${item.handle}" 등록에 실패했습니다.`);
        return false;
      }
    },
    [batchCountry, exchangeRates, showSuccess, showError, refreshExistingCreator, setMetricsForCreator]
  );

  // 여러 명 일괄: 한 명씩 순차로 수집 → 존재하면 바로 등록(비용/부하 관리를 위해 순차 처리).
  // items: [{ id(=이미 정리된 핸들, 화면 표시/업데이트 키), handle, platform }] — platform은
  // 줄마다 다를 수 있음(URL 붙여넣었을 때 도메인으로 자동 판별된 값, 호출부에서 결정해서 넘김).
  // onItemUpdate(status)로 진행 상황을 그때그때 호출부(모달)에 알려준다 — 별도 그리드 없이
  // 같은 모달 안에서 실시간으로 보여주기 위함. 존재하지 않는 계정은 'skipped'로 알려주고 끝냄.
  const collectAndRegisterBulk = useCallback(
    async (items, onItemUpdate) => {
      if (!batchCountry) {
        showError('먼저 국가를 지정해주세요.');
        return { found: 0, registered: 0, total: items.length, skipped: items.length };
      }
      let found = 0;
      let registeredCount = 0;
      for (const { id, handle, platform } of items) {
        onItemUpdate?.({ id, status: 'checking' });
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await collectCreatorMetrics(platform, [handle]);
          const result = res?.results?.[0];
          if (result?.ok) {
            found += 1;
            onItemUpdate?.({ id, status: 'registering', item: result });
            // eslint-disable-next-line no-await-in-loop
            const ok = await registerCreator(result);
            if (ok) registeredCount += 1;
            onItemUpdate?.({ id, status: ok ? 'registered' : 'failed', item: result });
          } else {
            onItemUpdate?.({ id, status: 'skipped' });
          }
        } catch {
          onItemUpdate?.({ id, status: 'skipped' });
        }
      }
      return { found, registered: registeredCount, total: items.length, skipped: items.length - found };
    },
    [batchCountry, registerCreator, showError]
  );

  const getRecentMetrics = useCallback(
    (platform, handle) => recentMetrics[metricsKey(platform, handle)] || null,
    [recentMetrics]
  );

  return (
    <CreatorCollectionContext.Provider
      value={{ registerCreator, collectAndRegisterBulk, batchCountry, setBatchCountry, getRecentMetrics, setMetricsForCreator }}
    >
      {children}
    </CreatorCollectionContext.Provider>
  );
}

export function useCreatorCollection() {
  const ctx = useContext(CreatorCollectionContext);
  if (!ctx) throw new Error('useCreatorCollection must be used within CreatorCollectionProvider');
  return ctx;
}
